import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Grid, Text, csx } from '@vtex/admin-ui'

import { DrawerFooter, DrawerBody } from '../../../../../../ui/components'
import type { Package } from '../../../../../../core/typings'
import { MethodSelectItem } from '../MethodSelectItem'
import { DeliveryMethodIcon } from './DeliveryMethodIcon'
import { formatAddressToExtended } from '../../../../utils'
import { useOrderFormShippingEstimate } from '../../../../hooks'
import { DeliveryChannel } from '../../../../constants'
import { useShippingActionsLegacy } from '../../../../../../checkout-instore-bridge/hooks/shippingActionsLegacy'
import { DistanceLabel } from './DistanceLabel'
import { StepSubmitButton } from '../../../../components'

export type PickupsPointsContentProps = {
  currentPackage: Package
  onPickupSubmit: (selectedPickupId: string) => void
  isOpen: boolean
  pickupPointsList: SLA[]
}

export const PickupsPointsContent = ({
  currentPackage,
  onPickupSubmit,
  isOpen,
  pickupPointsList,
}: PickupsPointsContentProps) => {
  const { getEstimateMessage } = useOrderFormShippingEstimate()
  const { refreshPickupPoints } = useShippingActionsLegacy()

  const getInitialSelectedPickupId = () => {
    return currentPackage.selectedSlaType === DeliveryChannel.PickupInPoint
      ? currentPackage.selectedSlaObj?.id ?? ''
      : null
  }

  const [selectedPickupId, setSelectedPickupId] = useState(() =>
    getInitialSelectedPickupId()
  )

  const lastSelectedSlaIdRef = useRef(selectedPickupId)

  const handlePickupPointClick = (pickupId: Optional<string>) => () => {
    if (pickupId) {
      setSelectedPickupId(pickupId)
    }
  }

  const handlePickupPointSubmit = async () => {
    lastSelectedSlaIdRef.current = selectedPickupId

    if (selectedPickupId) {
      onPickupSubmit(selectedPickupId)
    }
  }

  useEffect(() => {
    if (isOpen) {
      refreshPickupPoints()
    }
  }, [isOpen, refreshPickupPoints])

  useEffect(() => {
    setSelectedPickupId(
      isOpen ? lastSelectedSlaIdRef.current : currentPackage.selectedSlaType
    )
  }, [isOpen, currentPackage.selectedSlaType])

  return (
    <>
      <DrawerBody>
        <Grid rowGap="0.5rem">
          {pickupPointsList.map((pickupPoint) => (
            <MethodSelectItem
              key={pickupPoint.id}
              icon={<DeliveryMethodIcon />}
              title={pickupPoint.pickupStoreInfo?.friendlyName ?? ''}
              selected={pickupPoint.id === selectedPickupId}
              onClick={handlePickupPointClick(pickupPoint.id ?? '')}
              middleColumn={
                <Text>
                  <Text tone="secondary" variant="detail">
                    {formatAddressToExtended({
                      street: pickupPoint.pickupStoreInfo?.address?.street,
                      number: pickupPoint.pickupStoreInfo?.address?.number,
                      city: pickupPoint.pickupStoreInfo?.address?.city,
                      state: pickupPoint.pickupStoreInfo?.address?.state,
                    })}
                  </Text>
                  {pickupPoint.shippingEstimate && (
                    <Text
                      variant="action2"
                      className={csx({
                        marginTop: '$space-2',
                        display: 'block',
                      })}
                    >
                      {getEstimateMessage(pickupPoint.shippingEstimate)}
                    </Text>
                  )}
                </Text>
              }
              rightColumn={
                <DistanceLabel
                  sla={pickupPoint}
                  selectedSlaId={selectedPickupId}
                />
              }
            />
          ))}
        </Grid>
      </DrawerBody>
      <DrawerFooter>
        <StepSubmitButton
          disabled={!selectedPickupId}
          onClick={handlePickupPointSubmit}
          data-testid="pickups-points-content-submit-button"
        >
          <FormattedMessage id="continue" />
        </StepSubmitButton>
      </DrawerFooter>
    </>
  )
}
